import React, { useEffect, useState } from "react";
import { Bell, BellOff, Send, Loader2 } from "lucide-react";
import { Panel } from "./Tactical";
import { useT } from "../context/LanguageContext";
import {
  pushSupported,
  pushPermission,
  ensurePushRegistered,
  unregisterPush,
  sendTestPush,
} from "../lib/push";

/** Compact opt-in panel — shown on the Account page and in any other settings UI. */
export default function PushSettings() {
  const { t } = useT();
  const [supported] = useState(pushSupported());
  const [perm, setPerm] = useState(pushSupported() ? pushPermission() : "unsupported");
  const [busy, setBusy] = useState(false);
  const [hasToken, setHasToken] = useState(
    typeof window !== "undefined" && !!window.localStorage.getItem("eplay_fcm_token")
  );

  useEffect(() => {
    if (perm === "granted" && !hasToken) {
      ensurePushRegistered({ silent: true }).then((tok) => setHasToken(!!tok));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = async () => {
    setBusy(true);
    const tok = await ensurePushRegistered();
    setBusy(false);
    setPerm(pushPermission());
    setHasToken(!!tok);
  };

  const disable = async () => {
    setBusy(true);
    await unregisterPush();
    setBusy(false);
    setHasToken(false);
  };

  const test = async () => {
    setBusy(true);
    await sendTestPush();
    setBusy(false);
  };

  if (!supported) {
    return (
      <Panel>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">{t("push.kicker")}</div>
        <div className="flex items-start gap-3" data-testid="push-unsupported">
          <BellOff className="w-4 h-4 text-[#71717A] mt-0.5" />
          <div className="text-sm text-[#71717A]">
            {t("push.unsupported")}
          </div>
        </div>
      </Panel>
    );
  }

  const statusColor = perm === "granted" && hasToken ? "#34C759" : perm === "denied" ? "#E11D48" : "#A1A1AA";
  const statusText =
    perm === "granted" && hasToken
      ? t("push.on")
      : perm === "denied"
      ? t("push.blocked")
      : t("push.off");

  return (
    <Panel>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">{t("push.kicker")}</div>
      <div className="space-y-4" data-testid="push-settings">
        <div className="flex items-center gap-3">
          <Bell className="w-4 h-4 text-[#7C3AED]" />
          <div className="flex-1">
            <div className="font-heading text-sm uppercase tracking-wider">{t("push.title")}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: statusColor }}>
              {statusText}
            </div>
          </div>
        </div>
        <p className="text-xs text-[#71717A] leading-relaxed">
          {t("push.description")}
        </p>
        <div className="flex flex-wrap gap-2">
          {perm === "granted" && hasToken ? (
            <>
              <button
                type="button"
                onClick={test}
                disabled={busy}
                data-testid="push-test-btn"
                className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {t("push.test")}
              </button>
              <button
                type="button"
                onClick={disable}
                disabled={busy}
                data-testid="push-disable-btn"
                className="inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"
              >
                <BellOff className="w-3.5 h-3.5" /> {t("push.disable")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enable}
              disabled={busy || perm === "denied"}
              data-testid="push-enable-btn"
              className="inline-flex items-center gap-2 bg-[#EA580C] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#C2410C] transition-colors"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              {t("push.enable")}
            </button>
          )}
        </div>
      </div>
    </Panel>
  );
}
